import React from 'react';
import { Tooltip, TooltipProps } from '@mui/material';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { InfoCircle } from '@untitledui-pro/icons/line';
import { COLUMN_WIDTH } from '../panels/settings/SidePaneDialog';

type CustomTooltipMode = 'hover' | 'icon';
type TooltipSide = 'left' | 'right' | undefined;

type CustomTooltipProps = Omit<TooltipProps, 'children'> & {
    children?: React.ReactNode;
    mode?: CustomTooltipMode;
    iconOffset?: number;
    customOffset?: number;
    iconProps?: Omit<React.ComponentPropsWithoutRef<typeof InfoCircle>, 'ref'>;
    popperSx?: SxProps<Theme>;
    fullWidth?: boolean;

    /** Facoltativo: indica in quale colonna vive la tooltip (per boundary). */
    side?: TooltipSide;
};

const DEFAULT_ICON_OFFSET = 2;
const CustomTooltipOffsetContext = React.createContext(false);
const resolveSelectedPipelineBlockOffset = (root: HTMLSpanElement | null): number => {
    if (!root) return 0;

    const expandedBlock = root.closest('.pipeline-block--selected.pipeline-block--expanded') as HTMLElement | null;
    if (!expandedBlock) return 0;

    const rootRect = root.getBoundingClientRect();
    const blockRect = expandedBlock.getBoundingClientRect();

    return Math.max(0, Math.ceil(blockRect.bottom - rootRect.bottom) - 8);
};

const CustomTooltip: React.FC<CustomTooltipProps> = (props) => {
    const {
        children,
        mode = 'hover',
        iconOffset = DEFAULT_ICON_OFFSET,
        customOffset = 0,
        iconProps,
        side,
        popperSx,
        fullWidth = false,
        ...rest
    } = props;

    const { title, ...tooltipProps } = rest;

    const hasTitle = title !== undefined && title !== null && title !== false && !(typeof title === 'string' && title.trim().length === 0);
    const rootRef = React.useRef<HTMLSpanElement | null>(null);
    const isPipelineOffsetEnabled = React.useContext(CustomTooltipOffsetContext);
    const [boundaryEl, setBoundaryEl] = React.useState<HTMLElement | null>(null);

    React.useEffect(() => {
        if (typeof document === 'undefined') return;
        const closestPaper = rootRef.current?.closest('.MuiPaper-root') as HTMLElement | null;
        const drawerBoundary =
            side === undefined
                ? null
                : ((document.querySelector(
                      side === 'left' ? '.gui-drawer-paper--left' : '.gui-drawer-paper--right'
                  ) as HTMLElement | null) ?? (document.querySelector('.gui-drawer-paper') as HTMLElement | null));
        const nextBoundary = side ? (drawerBoundary ?? closestPaper ?? null) : (closestPaper ?? drawerBoundary ?? null);
        setBoundaryEl(nextBoundary);
    }, [side]);

    if (!hasTitle) return <>{children}</>;

    const basePopperSx: SxProps<Theme> = (theme) => {
        const base = (theme?.zIndex?.tooltip ?? 1500) + 1;
        return { zIndex: Math.max(base, 12000) };
    };

    const mergedPopperSx: SxProps<Theme> = Array.isArray(popperSx)
        ? [basePopperSx, ...popperSx]
        : popperSx
          ? [basePopperSx, popperSx]
          : basePopperSx;

    const commonTooltipProps: Partial<TooltipProps> = {
        PopperProps: {
            disablePortal: false,
            modifiers: [
                {
                    name: 'preventOverflow',
                    options: {
                        boundary: boundaryEl ?? 'clippingParents',
                        padding: 8,
                        altAxis: true,
                        tether: true,
                    },
                },
                {
                    name: 'flip',
                    options: {
                        boundary: boundaryEl ?? 'clippingParents',
                        padding: 8,
                    },
                },
                {
                    name: 'customOffset',
                    enabled: customOffset !== 0 || isPipelineOffsetEnabled,
                    phase: 'main',
                    fn: ({ state }) => {
                        if (!state.modifiersData.popperOffsets) return;
                        const baseOffset = customOffset ?? 0;
                        const autoOffset = isPipelineOffsetEnabled ? resolveSelectedPipelineBlockOffset(rootRef.current) : 0;
                        state.modifiersData.popperOffsets.y += baseOffset + autoOffset;
                    },
                },
            ],
        },
        componentsProps: {
            popper: {
                sx: mergedPopperSx,
            },
            tooltip: {
                sx: {
                    maxWidth: side ? COLUMN_WIDTH - 24 : 900,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    pointerEvents: 'auto',
                },
            },
        },
    };

    /* ================= ICON MODE ================= */

    if (mode === 'icon') {
        const { className: providedClassName, style: providedStyle, ...restIconProps } = iconProps ?? {};
        const iconClassName = ['icon-size-small', providedClassName].filter(Boolean).join(' ') || undefined;

        const iconWrapperStyle: React.CSSProperties = {
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 0,
            ...(children ? { marginLeft: iconOffset } : {}),
        };

        const infoIcon = (
            <Tooltip title={title} {...commonTooltipProps} {...tooltipProps}>
                <span style={iconWrapperStyle}>
                    <InfoCircle className={iconClassName} style={providedStyle} {...restIconProps} />
                </span>
            </Tooltip>
        );

        if (!children)
            return (
                <span ref={rootRef} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {infoIcon}
                </span>
            );

        return (
            <span ref={rootRef} style={{ display: 'inline-flex', alignItems: 'center' }}>
                {children}
                {infoIcon}
            </span>
        );
    }

    /* ================= HOVER MODE ================= */

    const content = React.isValidElement(children) ? (
        children
    ) : (
        <span style={{ display: fullWidth ? 'flex' : 'inline-flex', width: fullWidth ? '100%' : undefined }}>{children}</span>
    );

    return (
        <span
            ref={rootRef}
            style={{ display: fullWidth ? 'flex' : 'inline-flex', alignItems: 'center', width: fullWidth ? '100%' : undefined }}
        >
            <Tooltip title={title} {...commonTooltipProps} {...tooltipProps}>
                {content}
            </Tooltip>
        </span>
    );
};

export const CustomTooltipOffsetProvider = CustomTooltipOffsetContext.Provider;
export default CustomTooltip;
